import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  House, MagicWand, Hash, TextT, Image as ImageIcon, TrendUp,
  FolderOpen, ChartLine, Gear, ShieldCheck, SignOut, Lightning, FilmStrip,
  Microphone, Eye, VideoCamera, CalendarBlank, Crown, List, X
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Notifications } from "../components/Notifications";
import { AdminAIHealthBanner } from "../components/AdminAIHealthBanner";

const nav = [
  { to: "/app", end: true, icon: House, label: "Dashboard" },
  { to: "/app/script", icon: MagicWand, label: "Script Studio" },
  { to: "/app/caption", icon: Hash, label: "Captions" },
  { to: "/app/titles", icon: TextT, label: "Titles & Hooks" },
  { to: "/app/thumbnail", icon: ImageIcon, label: "Thumbnails" },
  { to: "/app/voiceover", icon: Microphone, label: "Voiceovers" },
  { to: "/app/broll", icon: VideoCamera, label: "B-Roll AI" },
  { to: "/app/editor", icon: FilmStrip, label: "Editor" },
  { to: "/app/competitor", icon: Eye, label: "Competitor" },
  { to: "/app/trends", icon: TrendUp, label: "Trend Radar" },
  { to: "/app/workspace", icon: FolderOpen, label: "Workspace" },
  { to: "/app/calendar", icon: CalendarBlank, label: "Calendar" },
  { to: "/app/affiliate", icon: Crown, label: "Affiliate" },
  { to: "/app/analytics", icon: ChartLine, label: "Analytics" },
];

const bottomNav = [
  { to: "/app/settings", icon: Gear, label: "Settings" },
];

function NavItems({ user, onItemClick, handleLogout, idSuffix = "" }) {
  const tid = (label) => `sidebar-${label.toLowerCase().replace(/[\s&]+/g, "-")}${idSuffix}`;
  return (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? "bg-[#FF3B6B]/10 text-white border border-[#FF3B6B]/30"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`
            }
            data-testid={tid(n.label)}
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 space-y-1 border-t border-white/10">
        {user?.role === "admin" && (
          <>
            <NavLink
              to="/app/admin"
              onClick={onItemClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                  isActive ? "bg-[#FF3B6B]/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
              data-testid={`sidebar-admin${idSuffix}`}
            >
              <ShieldCheck size={18} /> Admin
            </NavLink>
            <NavLink
              to="/app/admin/payouts"
              onClick={onItemClick}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                  isActive ? "bg-[#FF3B6B]/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
              data-testid={`sidebar-admin-payouts${idSuffix}`}
            >
              <Crown size={18} /> Admin · Payouts
            </NavLink>
          </>
        )}
        {bottomNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onItemClick}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm ${
                isActive ? "bg-[#FF3B6B]/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
              }`
            }
            data-testid={tid(n.label)}
          >
            <n.icon size={18} /> {n.label}
          </NavLink>
        ))}
        <NavLink
          to="/pricing"
          onClick={onItemClick}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5"
          data-testid={`sidebar-pricing${idSuffix}`}
        >
          <Lightning size={18} /> Upgrade plan
        </NavLink>
        <NavLink
          to="/help"
          onClick={onItemClick}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5"
          data-testid={`sidebar-help${idSuffix}`}
        >
          <ShieldCheck size={18} /> Help & Support
        </NavLink>
        <div className="mt-3 p-3 glass rounded-lg">
          <p className="text-xs text-white/60">Credits</p>
          <div className="flex items-center justify-between mt-1">
            <span className="font-mono text-lg" data-testid={`credits-display${idSuffix}`}>{user?.credits ?? 0}</span>
            <span className="text-xs uppercase tracking-wider text-[#FF3B6B]" data-testid={`plan-display${idSuffix}`}>{user?.plan}</span>
          </div>
        </div>
        <button
          onClick={() => { onItemClick?.(); handleLogout(); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/60 hover:text-white hover:bg-white/5"
          data-testid={`sidebar-logout${idSuffix}`}
        >
          <SignOut size={18} /> Sign out
        </button>
      </div>
    </>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/"); };

  // Close mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white" data-testid="dashboard-layout">
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 border-r border-white/10 bg-[#0c0c0e] sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#FF3B6B] flex items-center justify-center shadow-[0_0_24px_-4px_#FF3B6B]">
              <Lightning weight="fill" className="text-white" size={18} />
            </div>
            <span className="font-semibold tracking-tight">ClipForge<span className="text-[#FF3B6B]">AI</span></span>
          </div>
        </div>
        <NavItems user={user} handleLogout={handleLogout} idSuffix="-desktop" />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-overlay"
          />
          <aside className="relative flex flex-col w-72 max-w-[80vw] bg-[#0c0c0e] border-r border-white/10 h-full animate-in slide-in-from-left duration-200">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-[#FF3B6B] flex items-center justify-center shadow-[0_0_24px_-4px_#FF3B6B]">
                  <Lightning weight="fill" className="text-white" size={18} />
                </div>
                <span className="font-semibold tracking-tight">ClipForge<span className="text-[#FF3B6B]">AI</span></span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="text-white/60 hover:text-white p-1"
                aria-label="Close menu"
                data-testid="mobile-drawer-close"
              >
                <X size={22} />
              </button>
            </div>
            <NavItems user={user} handleLogout={handleLogout} onItemClick={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0">
        <AdminAIHealthBanner />
        <div className="flex items-center gap-2 p-4 border-b border-white/10 sticky top-0 bg-[#0A0A0A]/80 backdrop-blur z-30">
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden text-white/70 hover:text-white p-1 -ml-1"
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
          >
            <List size={24} />
          </button>
          <div className="md:hidden flex items-center gap-2 flex-1">
            <div className="w-7 h-7 rounded-md bg-[#FF3B6B] flex items-center justify-center"><Lightning weight="fill" size={14} /></div>
            <span className="font-semibold">ClipForge<span className="text-[#FF3B6B]">AI</span></span>
          </div>
          <div className="flex-1 hidden md:block" />
          <Notifications />
          {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" data-testid="user-avatar" />}
        </div>
        <Outlet />
      </main>
    </div>
  );
}
